import axios from 'axios'

export const AUTH_TOKEN_STORAGE_KEY = 'pos_auth_token_v1'

/**
 * Cliente HTTP central del frontend.
 *
 * - Define baseURL (Vite env) para apuntar al backend.
 * - Inyecta Authorization: Bearer <token> si existe en localStorage.
 * - Si hay 401, limpia token y emite evento global para que el AuthProvider reaccione.
 *
 * Regla importante:
 * - El backend sigue siendo la fuente de verdad para autorizaciones (RBAC).
 * - El cliente sólo adjunta token y normaliza el flujo de sesión.
 */
type GlobalWithProcessEnv = typeof globalThis & {
  process?: { env?: { VITE_API_BASE_URL?: string } }
}

const resolveBaseUrl = () => {
  const viteBase = import.meta.env.VITE_API_BASE_URL
  const nodeBase = (globalThis as unknown as GlobalWithProcessEnv).process?.env?.VITE_API_BASE_URL
  return viteBase ?? nodeBase ?? ''
}

export const http = axios.create({
  baseURL: resolveBaseUrl(),
  headers: {
    Accept: 'application/json',
  },
})

http.interceptors.request.use(config => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) : null
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object'

http.interceptors.response.use(
  res => res,
  (error: unknown) => {
    const errObj = isRecord(error) ? error : null
    const response = errObj && isRecord(errObj.response) ? errObj.response : null
    const status = response && typeof response.status === 'number' ? response.status : null
    if (status === 401 && typeof localStorage !== 'undefined') {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('pos-auth-changed'))
      }
    }
    return Promise.reject(error)
  }
)

